<script>
// Métricas do shell (prop `shell`), derivadas dos mocks aprovados em
// .specify/design-basemodal/spec-001-base-modal-shell-redesign/design/.
// O card do shell não cresce além da viewport: header e rodapé ficam fixos e
// só a região de conteúdo rola.
const SHELL_MAX_HEIGHT = '90vh'
const SHELL_BORDER_RADIUS = '12px'
const SHELL_CLOSE_ICON_SIZE = 24
// Shell lateral (padrão): o card encosta na borda direita, então ocupa a altura
// inteira e não tem raio — igual ao drawer legado. O `default-right-dialog` já
// zera o raio via CSS, mas o style inline do card venceria a regra.
const SHELL_DRAWER_HEIGHT = '100vh'
const SHELL_DRAWER_BORDER_RADIUS = '0'

export default {
  props: {
    bus: {
      type: Object,
      default: null,
    },
    buttonTitle: {
      type: String,
      default: 'Abrir modal',
    },
    maxWidth: {
      type: String,
      default: '600px',
    },
    height: {
      type: String,
      default: '400px',
    },
    contentClass: {
      type: String,
      default: null,
    },
    persistent: {
      type: Boolean,
      default: false,
    },
    destroyOnClose: {
      type: Boolean,
      default: false,
    },
    centerScreen: {
      type: Boolean,
      default: false,
    },
    centerScreenFullHeight: {
      type: Boolean,
      default: false,
    },
    centerAndDecreasesPadding: {
      type: Boolean,
      default: false,
    },
    backgroundColor: {
      type: String,
      default: '',
    },
    closeColor: {
      type: String,
      default: '#8E8E8E',
    },
    dontClose: {
      type: Boolean,
      default: false,
    },
    withoutCloseButton: {
      type: Boolean,
      default: false,
    },
    fixedActions: {
      type: Boolean,
      default: false,
    },
    // Mostra "* Campos obrigatórios" no canto esquerdo do rodapé (junto às ações).
    requiredHint: {
      type: Boolean,
      default: false,
    },
    // Sobrescreve o padding calculado do v-card (cardPadding). null preserva
    // o comportamento padrão.
    paddingOverride: {
      type: String,
      default: null,
    },
    // Sobrescreve o padding da linha de título/botão-fechar. null preserva o
    // comportamento padrão.
    titleRowPaddingOverride: {
      type: String,
      default: null,
    },
    // Ativa o shell de três regiões (header fixo / conteúdo sobre superfície
    // cinza / rodapé fixo). Sem ela, o render permanece idêntico ao legado —
    // premissa da adoção gradual nos 500+ modais já existentes.
    shell: {
      type: Boolean,
      default: false,
    },
    // Abre o modal a partir do canto inferior direito (dialog-bottom-transition),
    // mantendo o visual do shell (header/footer fixos, conteúdo com scroll).
    shellCorner: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialog: false,
      primaryActionId: null,
    }
  },
  computed: {
    // O shell segue a mesma ancoragem do modal legado: lateral por padrão
    // (dialog-right-transition + default-right-dialog) e centralizado sob
    // `centerScreen`. O shellCorner abre do canto com dialog-bottom-transition.
    isCentered() {
      if (this.shellCorner) return false
      return this.centerScreen
    },
    // Shell na ancoragem lateral — o único modo em que o card precisa colar na
    // borda e ocupar a viewport inteira. centerScreen e shellCorner flutuam
    // sobre o scrim e mantêm o raio e o teto de 90vh.
    isShellDrawer() {
      return this.shell && !this.centerScreen && !this.shellCorner
    },
    shellCloseIconSize() {
      return SHELL_CLOSE_ICON_SIZE
    },

    bgCard() {
      if (this.backgroundColor && this.backgroundColor.length) {
        return this.backgroundColor
      }
      return this.centerAndDecreasesPadding ? '#EEEEEE' : 'white'
    },

    cardClasses() {
      // Early return: o objeto do caminho legado não ganha chaves novas — há
      // teste que compara o retorno por igualdade estrita.
      if (this.shell) {
        return { 'd-flex flex-column': true, 'base-modal--shell': true }
      }
      return {
        'd-flex flex-column': this.fixedActions,
      }
    },
    cardStyle() {
      // O shell tem layout próprio (3 regiões em flex-coluna) e não entra na
      // matriz de padding/altura do legado.
      if (this.shell) {
        if (this.isShellDrawer) {
          return {
            height: SHELL_DRAWER_HEIGHT,
            'max-height': SHELL_DRAWER_HEIGHT,
            overflow: 'hidden',
            padding: '0',
            borderRadius: SHELL_DRAWER_BORDER_RADIUS,
          }
        }
        return {
          'max-height': SHELL_MAX_HEIGHT,
          overflow: 'hidden',
          padding: '0',
          borderRadius: SHELL_BORDER_RADIUS,
        }
      }
      return {
        'min-height': this.cardMinHeight,
        height: this.cardHeight,
        'max-height': this.cardMaxHeight,
        overflow: this.cardOverflow,
        padding: this.cardPadding,
        borderRadius: this.centerAndDecreasesPadding ? '0px' : '',
        backgroundColor: this.bgCard,
      }
    },
    cardMinHeight() {
      if (this.fixedActions) return undefined
      if (!this.centerScreen) return '100vh'
      return this.centerScreenFullHeight ? '90vh' : '10vh'
    },
    cardHeight() {
      // Drawer lateral com fixedActions ocupa a tela toda (100vh). No centerScreen
      // NÃO força altura — usa max-height (cardMaxHeight) p/ ficar centralizado e
      // crescer com o conteúdo até o teto, daí o conteúdo rola por dentro.
      return this.fixedActions && !this.centerScreen ? '100vh' : undefined
    },
    // centerScreen + fixedActions: modal centralizado com scroll INTERNO — cresce
    // até 90vh e o conteúdo (.base-modal__content--scrollable) rola por dentro,
    // com título e ações fixos. Evita o modal inteiro estourar a viewport.
    cardMaxHeight() {
      return this.fixedActions && this.centerScreen ? '90vh' : undefined
    },
    cardOverflow() {
      return this.fixedActions ? 'hidden' : undefined
    },
    cardPadding() {
      // == (não ===): cobre tanto o default real da prop (null) quanto o
      // contexto de teste unitário chamando o computed via .call({}) sem
      // declarar a chave (undefined) — ambos devem cair no cálculo padrão.
      if (this.paddingOverride != null) return this.paddingOverride
      if (!this.centerAndDecreasesPadding) return '34px 30px 20px'
      // Modais centralizados (npm run new modal) precisam de padding superior
      // para o título não ficar colado no topo do card.
      return this.centerScreenFullHeight ? '0 0 0px' : '0 0 25px'
    },
    showContent() {
      return !this.destroyOnClose || this.dialog
    },
    actionsStyle() {
      if (!this.fixedActions) return {}
      const actionsHeight = '96px'
      return {
        'min-height': actionsHeight,
        'flex-shrink': '0',
      }
    },
    titleRowPadding() {
      if (this.titleRowPaddingOverride != null) {
        return this.titleRowPaddingOverride
      }
      return this.centerAndDecreasesPadding ? '8px 8px 0px 0px' : ''
    },
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        this.primaryActionId = null
        if (this.bus) {
          this.bus.$emit('open')
        }
      } else {
        this.bus && this.bus.$emit('close')
      }

      // Com isso temos a possibilidade de adicionar `v-model` no base-modal e
      // receber a atualizacao se o modal está sendo apresentado ou nao
      this.$emit('input', newVal)
    },
    // Bug real: trocar de rota/módulo (ex.: navegacao-v2-sidebar client-entrance)
    // com o modal já aberto ou ainda abrindo destrói este host ANTES do
    // v-dialog processar isActive=false — o overlay/scrim do Vuetify (que só é
    // removido pela própria reatividade do v-dialog) fica órfão preso na tela.
    // Fechando aqui, como reação ao $route mudando (não em beforeDestroy, que
    // roda tarde demais pra reatividade do Vuetify processar antes do unmount),
    // o v-dialog ainda está montado pra rodar sua própria limpeza do overlay.
    // `to.name !== from.name` restringe à navegação DE VERDADE (rota/módulo
    // diferente): um `router.replace` na MESMA rota (ex.: deep-link `?param=`
    // limpando a própria query depois de abrir um modal) não é "trocar de
    // tela" e não pode fechar o modal que acabou de abrir — regressão real
    // 2026-08-06 no deep-link de transcrições (overlay órfão sem conteúdo).
    $route(to, from) {
      if (this.dialog && to.name !== from.name) {
        this.dialog = false
      }
    },
  },
  created() {
    if (this.bus) {
      this.bus.$on('dialog', this.open)
    }
  },
  beforeDestroy() {
    if (this.bus) {
      this.bus.$off('dialog', this.open)
    }
    this.forceRemoveOrphanOverlay()
  },
  methods: {
    // `hasSlot*`/`hasFooter`/`hasTitle` são MÉTODOS chamados direto do
    // `<template>` de propósito (G-13, T097 — única exceção documentada à
    // regra "sem chamada de função no template"): `$slots`/`$scopedSlots` são
    // atribuídos sem reatividade a cada `_render()`, então um `computed`
    // calcularia UMA vez e travaria no valor do primeiro render — um
    // `<template v-if="cond" v-slot:actions>` cuja `cond` nasce `false` nunca
    // mostraria a barra de ações (medido em produção; regressão travada em
    // `_base-modal.unit.js`). Método reavalia a cada render, como o `render()`.
    hasSlotButton() {
      return !!this.$slots['button'] || !!this.$scopedSlots['button']
    },
    hasSlotActions() {
      return !!this.$slots['actions'] || !!this.$scopedSlots['actions']
    },
    hasSlotSubtitle() {
      return (
        !!(this.$slots && this.$slots.subtitle) ||
        !!(this.$scopedSlots && this.$scopedSlots.subtitle)
      )
    },
    hasSlotMeta() {
      return (
        !!(this.$slots && this.$slots.meta) ||
        !!(this.$scopedSlots && this.$scopedSlots.meta)
      )
    },
    hasSlotHeaderAside() {
      return (
        !!(this.$slots && this.$slots['header-aside']) ||
        !!(this.$scopedSlots && this.$scopedSlots['header-aside'])
      )
    },
    hasSlotFooterStatus() {
      return (
        !!(this.$slots && this.$slots['footer-status']) ||
        !!(this.$scopedSlots && this.$scopedSlots['footer-status'])
      )
    },
    // No shell, a ausência de rodapé é o que caracteriza o modo leitura — não
    // existe prop de modo. Um modal sem ações e sem status simplesmente não
    // desenha o rodapé.
    hasFooter() {
      return (
        this.hasSlotActions() ||
        this.hasSlotFooterStatus() ||
        this.requiredHint
      )
    },
    hasTitle() {
      return (
        !!(this.$slots && this.$slots.title) ||
        !!(this.$scopedSlots && this.$scopedSlots.title)
      )
    },
    open(v) {
      this.dialog = v
    },
    close() {
      this.$emit('close')

      if (!this.dontClose) {
        this.dialog = false
      }
    },
    setPrimaryAction(actionId) {
      this.primaryActionId = actionId
    },
    isPrimaryAction(actionId) {
      return this.primaryActionId === actionId
    },
    // Bug real do Vuetify 2.0.11 (mixins/overlayable): o <div class="v-overlay">
    // é criado como uma instância Vue avulsa e inserida manualmente no DOM
    // (fora da árvore de componentes — Vue nunca a remove ao destruir este
    // host). Ao abrir, genOverlay() cria o overlay com value:false e só marca
    // value:true um frame depois via requestAnimationFrame. Se o host é
    // destruído (troca de rota/módulo) DENTRO dessa janela — exatamente o
    // caso de clicar num item client-centric e já trocar de módulo antes do
    // modal abrir —, removeOverlay() seta overlay.value=false, que JÁ é
    // false: nenhuma transição dispara, e a remoção do DOM depende de um
    // listener de 'transitionend' que nunca roda. O overlay fica órfão na
    // tela para sempre (cada tentativa nova empilha mais um por cima).
    // Remove o nó diretamente aqui, sem depender da transição — idempotente:
    // não faz nada se o Vuetify já tiver limpado normalmente.
    forceRemoveOrphanOverlay() {
      const dialogRef = this.$refs.dialog
      const overlay = dialogRef && dialogRef.overlay
      if (overlay && overlay.$el && overlay.$el.parentNode) {
        overlay.$el.remove()
        overlay.$destroy()
      }
    },
  },
}
</script>

<template>
  <div data-app>
    <v-dialog
      ref="dialog"
      v-model="dialog"
      :max-width="maxWidth"
      :height="height"
      :persistent="persistent"
      :transition="isCentered ? null : (shellCorner ? 'dialog-bottom-transition' : 'dialog-right-transition')"
      :content-class="
        (isCentered ? '' : 'default-right-dialog') + ' ' + contentClass
      "
      role="dialog"
      aria-modal="true"
    >
      <template v-if="hasSlotButton()" v-slot:activator="{ on }">
        <BaseButton none v-on="on">
          <slot name="button" :open="on" />
        </BaseButton>
      </template>
      <v-card :class="cardClasses" :style="cardStyle">
        <template v-if="shell">
          <header class="base-modal__header">
            <!-- header-identity substitui o bloco inteiro da esquerda; sem ele,
                 vale a composição padrão avatar + título + subtítulo. -->
            <div class="base-modal__identity">
              <slot name="header-identity">
                <slot name="header-avatar" />
                <div class="base-modal__heading">
                  <div class="base-modal__title">
                    <slot name="title" />
                  </div>
                  <div v-if="hasSlotSubtitle()" class="base-modal__subtitle">
                    <slot name="subtitle" />
                  </div>
                </div>
              </slot>
            </div>
            <!-- Região livre entre o título e o fechar. Só existe quando há
                 conteúdo: um wrapper vazio consumiria `gap` dos dois lados. -->
            <div v-if="hasSlotHeaderAside()" class="base-modal__header-aside">
              <slot name="header-aside" />
            </div>
            <div class="base-modal__header-actions">
              <slot name="header-actions" />
              <v-btn
                v-if="!withoutCloseButton"
                icon
                aria-label="Fechar"
                @click="close"
              >
                <icon-svg
                  name="close-circle"
                  :color="closeColor"
                  :size="shellCloseIconSize"
                  class="modal-close-button"
                />
              </v-btn>
            </div>
          </header>
          <section class="base-modal__body">
            <div v-if="hasSlotMeta()" class="base-modal__meta">
              <slot name="meta" />
            </div>
            <slot v-if="showContent" />
          </section>
          <footer v-if="hasFooter()" class="base-modal__footer">
            <div class="base-modal__footer-status">
              <slot name="footer-status">
                <span v-if="requiredHint" class="az-ds-aux">
                  <span class="az-required-asterisk">*</span> Campos
                  obrigatórios
                </span>
              </slot>
            </div>
            <div class="base-modal__footer-actions">
              <slot name="actions" :close="close" :setPrimaryAction="setPrimaryAction" :isPrimaryAction="isPrimaryAction" />
            </div>
          </footer>
        </template>
        <template v-else>
          <div
            class="d-flex align-center"
            :class="{
              'justify-content-between': hasTitle(),
              'justify-content-end': !hasTitle(),
            }"
            :style="{
              padding: titleRowPadding,
            }"
          >
            <slot name="title" />
            <v-btn
              v-if="!withoutCloseButton"
              :small="centerAndDecreasesPadding"
              icon
              @click="close"
            >
              <icon-svg
                name="close-circle"
                :color="closeColor"
                :size="centerAndDecreasesPadding ? 22 : 30"
                class="modal-close-button"
              />
            </v-btn>
          </div>
          <div
            v-if="fixedActions && showContent"
            class="base-modal__content--scrollable"
          >
            <slot />
          </div>
          <slot v-if="!fixedActions && showContent" />
          <v-divider
            v-if="fixedActions && hasSlotActions()"
            class="base-modal__actions-divider"
          />
          <v-card-actions
            v-if="hasSlotActions()"
            class="modal-actions"
            :style="actionsStyle"
          >
            <span
              v-if="requiredHint"
              class="base-modal__required-hint az-ds-aux"
            >
              <span class="az-required-asterisk">*</span> Campos obrigatórios
            </span>
            <v-spacer />
            <slot name="actions" :close="close" />
            <v-spacer v-if="centerScreen" />
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<style lang="sass" scoped>
.modal-close-button
  cursor: pointer

.base-modal
  &__content--scrollable
    flex: 1
    overflow-y: auto

  // Hint "* Campos obrigatórios" no canto esquerdo do rodapé. O .modal-actions
  // usa justify-content:end (empurra tudo p/ direita); margin-right:auto absorve
  // o espaço livre e joga a hint p/ a esquerda total, mantendo os botões à direita.
  &__required-hint
    margin-right: auto
    white-space: nowrap

@import '@design/components/_base-modal-shell.sass'
</style>
